"use client"

import type React from "react"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { FaMinus } from "react-icons/fa"
import { GoPlus } from "react-icons/go"
import { useMediaQuery } from "@/hooks/use-mobile"

// Mock styled wrapper component
const StyledWrapper = ({ children }: { children: React.ReactNode }) => (
    <div className="prose prose-sm max-w-none">{children}</div>
)

// Shared content component to avoid duplication
const ServiceContent = ({ card, selectedIndex }: { card: any[]; selectedIndex: number }) => {
    if (selectedIndex === -1 || !card[selectedIndex]) return null

    const selectedCard = card[selectedIndex]

    return (
        <motion.div
            key={selectedIndex}
            className="flex flex-col text-homeblack px-3 pt-4 md:p-8 font-semibold text-[28px] rounded-lg justify-start items-start mb-6 relative"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            <p className="mb-5 text-[17px] md:text-[1.6rem] font-extrabold text-start text-blue md:mb-8">
                {selectedCard?.title}
            </p>

            <img
                src={selectedCard?.icon || "/images/priya/technique_banner.webp"}
                alt="Technique Banner"
                className="w-full min-h-[130px] h-auto rounded-lg mb-4"
            />

            <StyledWrapper>
                <div
                    className="text-homegrey px-0 md:px-0"
                    dangerouslySetInnerHTML={{
                        __html: selectedCard?.description || "No description available.",
                    }}
                />
            </StyledWrapper>
        </motion.div>
    )
}

interface ServiceAccordionProps {
    card: Array<{
        service_name?: string
        title?: string
        description?: string
        icon?: string
    }>
}

export default function ServiceAccordion({ card = [] }: ServiceAccordionProps) {
    const [selectedServiceIndex, setSelectedServiceIndex] = useState(0)
    const isMobile = useMediaQuery("(max-width: 768px)")

    const handleServiceClick = (index: number) => {
        setSelectedServiceIndex(index)
    }

    if (!card || card.length === 0) {
        return <div>No services available</div>
    }

    // Mobile Accordion View
    if (isMobile) {
        return (
            <div className="my-8">
                {card.map((elem, index) => (
                    <div key={index} className="border-b border-gray-300">
                        <button
                            className={`w-full text-left bg-white p-4 ${selectedServiceIndex === index ? "text-pink" : "text-homeblack"
                                } text-md font-semibold flex justify-between items-center transition-colors duration-300`}
                            onClick={() => handleServiceClick(index)}
                        >
                            {elem?.service_name || elem?.title}
                            <span className="transition-transform duration-300">
                                {selectedServiceIndex === index ? <FaMinus /> : <GoPlus />}
                            </span>
                        </button>

                        <motion.div
                            className="overflow-hidden bg-white"
                            initial={false}
                            animate={
                                selectedServiceIndex === index
                                    ? { height: "auto", opacity: 1, scale: 1 }
                                    : { height: 0, opacity: 0, scale: 0.9 }
                            }
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                        >
                            {selectedServiceIndex === index && (
                                <div className="p-4">
                                    <ServiceContent card={card} selectedIndex={selectedServiceIndex} />
                                </div>
                            )}
                        </motion.div>
                    </div>
                ))}
            </div>
        )
    }

    // Desktop Tabs View
    return (
        <div className="flex my-10 items-start justify-between gap-6">
            {/* Sidebar Tabs */}
            <div className="flex flex-col gap-3 md:w-[320px] lg:w-[300px]">
                {card.map((elem, index) => (
                    <motion.div
                        key={index}
                        onClick={() => handleServiceClick(index)}
                        className={`w-full text-center text-homeblack py-4 px-4 rounded-xl cursor-pointer transition-all duration-150 ease-in-out hover:shadow-lg ${selectedServiceIndex === index
                                ? "text-pink border-[1px] border-lightblue shadow-lg bg-lightblue/10"
                                : "bg-white"
                            }`}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.3, ease: "easeInOut", delay: index * 0.1 }}
                    >
                        <p className="!font-semibold text-[16px]">{elem?.service_name || elem?.title}</p>
                    </motion.div>
                ))}
            </div>

            {/* Content Panel */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={selectedServiceIndex}
                    className="bg-white w-full rounded-lg pb-8 shadow-md"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                    <ServiceContent card={card} selectedIndex={selectedServiceIndex} />
                </motion.div>
            </AnimatePresence>
        </div>
    )
}
